import {Palette} from 'hubbl-shared';
import {memo, ReactNode, useState} from 'react';
import styled from 'styled-components';
import {TextHeading6} from '../index';

interface Tab {
  id: string;
  label: string;
  content: ReactNode;
}

interface TabbedContainerProps {
  tabs: Tab[];
  defaultActiveTab?: string;
}

export const TabbedContainer = memo(
  ({tabs, defaultActiveTab}: TabbedContainerProps) => {
    const [activeTab, setActiveTab] = useState(
      defaultActiveTab || tabs[0]?.id || '',
    );

    const activeTabContent = tabs.find(tab => tab.id === activeTab)?.content;

    return (
      <Container>
        <TabHeader>
          {tabs.map(tab => (
            <TabButton
              key={tab.id}
              isActive={activeTab === tab.id}
              onClick={() => setActiveTab(tab.id)}>
              <TextHeading6
                color={
                  activeTab === tab.id
                    ? Palette.primaryTerraCotta
                    : Palette.almostBlack500
                }
                text={tab.label}
              />
            </TabButton>
          ))}
        </TabHeader>
        <TabContent>{activeTabContent}</TabContent>
      </Container>
    );
  },
);

const Container = styled.div`
  background: white;
  border-radius: 16px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
  transition: all 0.3s ease;
  position: relative;
  overflow: hidden;
  margin-bottom: 24px;

  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 12px 40px rgba(0, 0, 0, 0.15);
  }

  &::after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: linear-gradient(
      135deg,
      rgba(255, 255, 255, 0.1) 0%,
      transparent 50%
    );
    pointer-events: none;
    opacity: 0;
    transition: opacity 0.3s ease;
  }

  &:hover::after {
    opacity: 1;
  }
`;

const TabHeader = styled.div`
  display: flex;
  border-bottom: 1px solid ${Palette.almostBlackTransparent10};
  background: ${Palette.almostBlackTransparent5};
`;

const TabButton = styled.button<{isActive: boolean}>`
  flex: 1;
  padding: 16px 24px;
  background: ${props => (props.isActive ? Palette.white : 'transparent')};
  border: none;
  border-bottom: 3px solid
    ${props => (props.isActive ? Palette.primaryTerraCotta : 'transparent')};
  cursor: pointer;
  transition: all 0.2s ease;
  position: relative;

  &:hover {
    background: ${props =>
      props.isActive ? Palette.white : Palette.almostBlackTransparent5};
  }

  &:first-child {
    border-top-left-radius: 16px;
  }

  &:last-child {
    border-top-right-radius: 16px;
  }
`;

const TabContent = styled.div`
  padding: 24px;
`;
